import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ARTICLE_URL = "https://www.hey-lets-travel.com/community/eastenderz-hi-ibiza-7-july-2026";
const IMAGE_URL = "https://hiibiza.b-cdn.net/assets/3d7a04a5-f0e7-4a07-a003-a023e86fb133--2026_HI_EAST&DUBS_ANNOUNCE_WEBSITE_1920X1080.jpg";
const TICKET_URL = "https://www.hiibiza.com/events/2026/eastenderz/2026-07-07";
const CONCIERGE_URL = "/concierge";
const EVENTS_URL = "/events";

const THEATRE_LINEUP = ["East End Dubs", "Franky Rizardo", "Gaskin", "Ellia Jaya"];
const CLUB_ROOM_LINEUP = ["Paco Osuna", "Rendher", "Wheats"];
const ALL_ARTISTS = [...THEATRE_LINEUP, ...CLUB_ROOM_LINEUP];

const KEYWORDS = [
  "Eastenderz Hï Ibiza 7 July 2026 tickets",
  "East End Dubs presents Eastenderz Hï Ibiza",
  "Franky Rizardo Hï Ibiza",
  "Gaskin Hï Ibiza Eastenderz",
  "Ellia Jaya Hï Ibiza",
  "Paco Osuna Now Here Hï Ibiza",
  "Rendher Hï Ibiza",
  "Wheats Hï Ibiza",
  "Hï Ibiza Tuesday tickets",
  "Eastenderz Ibiza 2026",
  "HLT Ibiza concierge",
];

const FAQS = [
  {
    question: "When is East End Dubs presents Eastenderz at Hï Ibiza?",
    answer: "East End Dubs presents Eastenderz is at Hï Ibiza on Tuesday 7 July 2026, with the official event page listing a 23:30 start time.",
  },
  {
    question: "Who is playing Eastenderz at Hï Ibiza on 7 July 2026?",
    answer: "The Theatre lineup is East End Dubs, Franky Rizardo, Gaskin and Ellia Jaya. Club Room / Now Here lists Paco Osuna, Rendher and Wheats.",
  },
  {
    question: "Where can I buy Eastenderz Hï Ibiza tickets?",
    answer: "Use the official Hï Ibiza event page linked in this HLT guide. The ticket panel showed prices from 75 EUR when checked, but live availability and pricing can change.",
  },
  {
    question: "Where is Hï Ibiza?",
    answer: "Hï Ibiza is in Playa d'en Bossa, Ibiza, on the former Space Ibiza site, close to Ushuaïa Ibiza and the main resort strip.",
  },
];

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "East End Dubs presents Eastenderz at Hï Ibiza 7 July 2026: Tickets, Lineup and HLT Guide",
  description:
    "East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 7 July 2026 with East End Dubs, Franky Rizardo, Gaskin, Ellia Jaya and Now Here in Club Room.",
  image: [IMAGE_URL],
  mainEntityOfPage: ARTICLE_URL,
  datePublished: "2026-07-07",
  dateModified: "2026-07-07",
  author: { "@type": "Organization", name: "Hey Let's Travel" },
  publisher: { "@type": "Organization", name: "Hey Let's Travel" },
};

const eventSchema = {
  "@context": "https://schema.org",
  "@type": "MusicEvent",
  name: "East End Dubs presents Eastenderz at Hï Ibiza",
  startDate: "2026-07-07T23:30:00+02:00",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  eventStatus: "https://schema.org/EventScheduled",
  image: [IMAGE_URL],
  location: {
    "@type": "Place",
    name: "Hï Ibiza",
    address: "Playa d'en Bossa, Ibiza, Spain",
  },
  performer: ALL_ARTISTS.map((artist) => ({ "@type": "MusicGroup", name: artist })),
  offers: {
    "@type": "Offer",
    url: TICKET_URL,
    priceCurrency: "EUR",
    price: "75",
    availability: "https://schema.org/InStock",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

function TicketCta({ children = "Buy Eastenderz Tickets" }) {
  return (
    <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="btn-primary">
      {children} -&gt;
    </a>
  );
}

function ConciergeCta({ children = "Build the Trip With HLT" }) {
  return (
    <Link to={CONCIERGE_URL} className="btn-secondary">
      {children} -&gt;
    </Link>
  );
}

function ArticleSection({ title, children }) {
  return (
    <section className="space-y-4 border-t border-white/10 pt-8">
      <h2 className="font-display text-4xl leading-none text-white md:text-5xl">{title}</h2>
      <div className="space-y-4 text-base leading-8 text-slate-300">{children}</div>
    </section>
  );
}

export default function EastenderzHiIbizaSevenJulyArticle() {
  return (
    <>
      <Helmet>
        <title>Eastenderz Hï Ibiza 7 July 2026 Tickets | HLT Guide</title>
        <meta
          name="description"
          content="East End Dubs presents Eastenderz at Hï Ibiza on Tuesday 7 July 2026 with Franky Rizardo, Gaskin, Ellia Jaya, Paco Osuna, Rendher and Wheats. Official ticket link and HLT guide."
        />
        <meta name="keywords" content={KEYWORDS.join(", ")} />
        <link rel="canonical" href={ARTICLE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Eastenderz Hï Ibiza 7 July 2026 Tickets" />
        <meta property="og:description" content="Official ticket link, lineup and HLT planning guide for Eastenderz at Hï Ibiza." />
        <meta property="og:url" content={ARTICLE_URL} />
        <meta property="og:image" content={IMAGE_URL} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Eastenderz Hï Ibiza 7 July 2026 Tickets" />
        <meta name="twitter:description" content="East End Dubs, Franky Rizardo, Gaskin, Ellia Jaya and Now Here at Hï Ibiza." />
        <meta name="twitter:image" content={IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <article className="page space-y-10">
        <header className="grid gap-6 lg:grid-cols-[minmax(0,1fr),30rem] lg:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-500">HLT Essential · Ibiza Tonight</p>
            <h1 className="mt-4 font-display text-5xl leading-none text-white md:text-7xl">
              East End Dubs presents Eastenderz at Hï Ibiza: 7 July 2026
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-300">
              Eastenderz returns to Hï Ibiza tonight with East End Dubs leading The Theatre alongside Franky Rizardo, Gaskin and Ellia Jaya. Club Room / Now Here brings Paco Osuna, Rendher and Wheats for the tougher second-room thread.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <TicketCta children="Buy official Hï tickets" />
              <ConciergeCta children="Plan Hï Ibiza with HLT" />
            </div>
          </div>
          <aside className="border border-brand-500/25 bg-[#111111] p-5">
            <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Event details</p>
            <dl className="mt-4 grid gap-3 text-sm text-slate-300">
              <div><dt className="text-white">Event</dt><dd>East End Dubs presents Eastenderz</dd></div>
              <div><dt className="text-white">Venue</dt><dd>Hï Ibiza</dd></div>
              <div><dt className="text-white">Date</dt><dd>Tuesday 7 July 2026</dd></div>
              <div><dt className="text-white">Time</dt><dd>23:30</dd></div>
              <div><dt className="text-white">Tickets</dt><dd>From 75 EUR when checked</dd></div>
              <div><dt className="text-white">Sound</dt><dd>Deep tech, minimal house, rolling groove and Now Here pressure</dd></div>
            </dl>
          </aside>
        </header>

        <figure className="overflow-hidden border border-brand-500/20 bg-black">
          <img src={IMAGE_URL} alt="Eastenderz at Hï Ibiza 7 July 2026 artwork" className="w-full object-cover" />
        </figure>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr),20rem]">
          <div className="space-y-8">
            <ArticleSection title="Why tonight matters">
              <p>
                Eastenderz is one of Hï Ibiza's cleanest Tuesday bookings for people who want groove before spectacle. East End Dubs has built the brand around low-slung rhythm, patient drums and minimal detail, which gives The Theatre a different feel from the obvious big-room vocal nights on the island.
              </p>
              <p>
                Tonight's listing is strong because it gives Eastenderz a proper house spread. Franky Rizardo brings a warmer, more direct Dutch house touch, Gaskin adds modern UK club weight, and Ellia Jaya gives the room a fresh selector-led edge. It is a lineup that can move without losing the underground thread.
              </p>
            </ArticleSection>

            <ArticleSection title="The Theatre: East End Dubs, Franky Rizardo, Gaskin and Ellia Jaya">
              <p>
                East End Dubs is the anchor. Expect rolling bass pressure, clipped drums and long blends rather than instant-pop payoff. The set should make sense in Hï's Theatre because the production can lift the room while the music stays stripped and functional.
              </p>
              <p>
                Franky Rizardo is the smart contrast: more melody, more classic house swing, and enough Ibiza polish to widen the room. Gaskin pushes things back toward a modern tech-house and UK groove pocket, while Ellia Jaya rounds out the bill with a current, club-ready energy.
              </p>
            </ArticleSection>

            <ArticleSection title="Club Room / Now Here">
              <p>
                Club Room / Now Here is led by Paco Osuna, with Rendher and Wheats also listed. That room gives tonight a heavier second lane: more techno pressure, more percussion, and a darker shape for people who want to move away from The Theatre without losing intensity.
              </p>
              <p>
                The best plan is to use both rooms properly. Start with Eastenderz in The Theatre for the rolling groove, then step into Now Here when you want a tighter, tougher Hï Ibiza session.
              </p>
            </ArticleSection>

            <ArticleSection title="Ticket link and timing">
              <p>
                We are linking directly to the official Hï Ibiza event page for Eastenderz on 7 July 2026. The ticket panel showed entry from 75 EUR when checked, but Hï pricing and availability can move quickly on the day.
              </p>
              <p>
                Doors are listed at 23:30. Hï Ibiza is in Playa d'en Bossa, so sort your ticket first, then plan transport, pre-drinks and the room order before you arrive.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy tickets on Hï Ibiza" />
                <ConciergeCta children="Ask HLT to arrange the night" />
              </div>
            </ArticleSection>

            <ArticleSection title="Where to stay for Hï Ibiza">
              <p>
                Playa d'en Bossa is the obvious base if tonight is built around Hï. You are close to the venue, close to Ushuaïa, and close enough to recover without turning the night into a transport mission. Ibiza Town works better for polished dinners and marina energy before the club, while San Antonio is more of a budget or sunset-led choice and needs transport planned earlier.
              </p>
              <p>
                HLT can help with hotels, villas, transfers, tables and a wider Ibiza route around Eastenderz if you want the night handled cleanly.
              </p>
            </ArticleSection>

            <ArticleSection title="Event details at a glance">
              <ul className="grid gap-2 text-slate-300">
                <li><strong className="text-white">Event:</strong> East End Dubs presents Eastenderz</li>
                <li><strong className="text-white">Venue:</strong> Hï Ibiza, Playa d'en Bossa</li>
                <li><strong className="text-white">Date:</strong> Tuesday 7 July 2026</li>
                <li><strong className="text-white">Time:</strong> 23:30</li>
                <li><strong className="text-white">The Theatre:</strong> {THEATRE_LINEUP.join(", ")}</li>
                <li><strong className="text-white">Club Room / Now Here:</strong> {CLUB_ROOM_LINEUP.join(", ")}</li>
                <li><strong className="text-white">Tickets:</strong> From 75 EUR when checked</li>
                <li><strong className="text-white">Official ticket link:</strong> <a href={TICKET_URL} target="_blank" rel="noopener noreferrer" className="text-brand-500 underline underline-offset-4">Hï Ibiza event tickets</a></li>
              </ul>
            </ArticleSection>

            <ArticleSection title="Final word">
              <p>
                Eastenderz at Hï Ibiza tonight is for the people who want the island's big-club production with a more focused underground groove. The Theatre has East End Dubs and a strong supporting cast, while Now Here gives the night a harder second-room pull.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <TicketCta children="Buy Eastenderz tickets" />
                <Link to={EVENTS_URL} className="btn-secondary">Browse Ibiza events -&gt;</Link>
              </div>
            </ArticleSection>
          </div>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border border-brand-500/20 bg-[#111111] p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">HLT trip builder</p>
              <h3 className="mt-3 font-display text-3xl leading-none text-white">Lock the ticket, then build the night.</h3>
              <p className="mt-3 text-sm leading-6 text-slate-300">
                Need a hotel, villa, transfer, table or group route around Eastenderz at Hï Ibiza? HLT can shape the night around the event.
              </p>
              <div className="mt-4 grid gap-3">
                <TicketCta children="Official tickets" />
                <ConciergeCta children="Concierge request" />
              </div>
            </div>

            <div className="border border-white/10 bg-black/40 p-5">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-500">Good to know</p>
              <ul className="mt-3 grid gap-3 text-sm leading-6 text-slate-300">
                <li><strong className="text-white">Arrive with a plan:</strong> Hï has multiple rooms and a late start.</li>
                <li><strong className="text-white">Book official:</strong> use the Hï page for live ticket availability.</li>
                <li><strong className="text-white">Use both rooms:</strong> The Theatre gives the Eastenderz groove, Club Room brings Now Here pressure.</li>
              </ul>
            </div>
          </aside>
        </div>

        <section className="space-y-4 border-t border-white/10 pt-8">
          <h2 className="font-display text-4xl leading-none text-white md:text-5xl">FAQ</h2>
          <div className="grid gap-3">
            {FAQS.map((item) => (
              <details key={item.question} className="border border-white/10 bg-[#111111] p-4">
                <summary className="cursor-pointer font-semibold text-white">{item.question}</summary>
                <p className="mt-3 text-sm leading-6 text-slate-300">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </article>
    </>
  );
}
